import { Component, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { HeaderComponent } from '../../components/header/header.component';
import { BottomBarComponent } from '../../components/bottom-bar/bottom-bar.component';
import { IosInstallPromptComponent } from '../../components/ios-install-prompt/ios-install-prompt.component';
import { TransactionFormComponent } from '../../components/transaction-form/transaction-form.component';
import { SidebarService } from '../../services/sidebar/sidebar.service';
import { ScreenService } from '../../services/screen/screen.service';
import { TransactionService } from '../../services/transaction/transaction.service';
import { ToastService } from '../../services/toast/toast.service';
import { NewTransaction, PromptTransaction } from '../../models/transaction.model';
import { TEXT_CONSTANTS } from '../../constants/text-constants';
import { ActionService } from '../../services/action/action.service';

@Component({
  selector: 'main-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    SidebarComponent,
    HeaderComponent,
    BottomBarComponent,
    IosInstallPromptComponent,
    TransactionFormComponent
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.css'
})
export class MainLayoutComponent {
  private _sidebarService = inject(SidebarService);
  private _screenService = inject(ScreenService);
  private _transactionService = inject(TransactionService);
  private _toastService = inject(ToastService);
  private _actionService = inject(ActionService);
  private _router = inject(Router);

  isCollapsed$ = this._sidebarService.isCollapsed$;

  /** Controls the layout-level transaction form visibility (for mobile FAB). */
  isFormVisible = false;

  /** Whether the current view is mobile. */
  isMobile(): boolean {
    return this._screenService.isMobile();
  }

  /** Whether the current view is standalone PWA. */
  isStandalone(): boolean {
    return this._screenService.isStandalone();
  }

  /** Whether the mobile FAB should be visible on the current route. */
  shouldShowFab(): boolean {
    const url = this._router.url;
    return url.includes('/dashboard') || url.includes('/transactions') || url.includes('/fixed-expenses');
  }

  /** Toggles the transaction form visibility. */
  toggleForm(): void {
    this.isFormVisible = !this.isFormVisible;
  }

  /** Triggers context-aware action via ActionService for mobile FAB. */
  onFabTap(): void {
    this._actionService.triggerAction();
  }

  /** Handles transaction form submission. */
  onFormSubmit(form: NewTransaction): void {
    this.isFormVisible = false;
    this._transactionService.createTransaction(form).subscribe({
      next: () => {
        this._toastService.success(
          TEXT_CONSTANTS.COMMON.SUCCESS,
          TEXT_CONSTANTS.TRANSACTIONS.CREATE_SUCCESS
        );
      },
      error: (error) => {
        console.error('Error creating transaction:', error);
        this._toastService.error(
          TEXT_CONSTANTS.COMMON.ERROR,
          this._toastService.getErrorDetail(error, TEXT_CONSTANTS.TRANSACTIONS.CREATE_ERROR)
        );
      }
    });
  }

  /** Handles prompt-based transaction submission. */
  onPromptSubmit(prompt: PromptTransaction): void {
    this.isFormVisible = false;
    this._transactionService.createTransactionWithPrompt(prompt).subscribe({
      next: () => {
        this._toastService.success(
          TEXT_CONSTANTS.COMMON.SUCCESS,
          TEXT_CONSTANTS.TRANSACTIONS.CREATE_SUCCESS
        );
      },
      error: (error) => {
        console.error('Error creating transaction from prompt:', error);
        this._toastService.error(
          TEXT_CONSTANTS.COMMON.ERROR,
          this._toastService.getErrorDetail(error, TEXT_CONSTANTS.TRANSACTIONS.CREATE_ERROR)
        );
      }
    });
  }
}
